/**
 * TrialFollowupSettings — Probetraining Follow-Up (Iter 307)
 *
 * Admin-Setting: Aktiviert das System, das Kunden nach einem Probetraining
 * (ohne Folgebuchung) automatisch einen Gratis-Voucher für einen anderen
 * Trainer schickt.
 *
 * User-Wunsch Markus (06/2026):
 *   • Wartezeit konfigurierbar (Default 14 Tage)
 *   • Channels in der Vorlage (Template) einstellbar
 *   • Voucher gilt für alle anderen Trainer, die diesen Service anbieten
 *   • Wenn nur 1 Trainer den Service hat → kein Reminder
 *   • Voucher-Link → Auto-Booking (Customer klickt, sieht Slots, bucht gratis)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Play, Gift, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrialFollowupSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    wait_days: 14,
    voucher_validity_days: 30,
    eligible_service_ids: [],
    email_template_id: 'trial_followup_email',
    whatsapp_template_id: 'trial_followup_wa',
    send_email: true,
    send_whatsapp: false,
  });
  const [services, setServices] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, servicesRes, vouchersRes] = await Promise.all([
        axios.get(`${API}/admin/trial-followup/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/trial-followup/eligible-services`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/trial-followup/vouchers`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      if (settingsRes.data) setSettings((p) => ({ ...p, ...settingsRes.data }));
      if (servicesRes.data) setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      if (vouchersRes.data) setVouchers(Array.isArray(vouchersRes.data) ? vouchersRes.data : []);
    } catch (e) {
      console.error('Trial-Followup settings load err', e);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleService = (sid) => {
    setSettings((p) => {
      const arr = p.eligible_service_ids || [];
      return {
        ...p,
        eligible_service_ids: arr.includes(sid)
          ? arr.filter((x) => x !== sid)
          : [...arr, sid],
      };
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/trial-followup/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Probetraining Follow-Up gespeichert');
      fetchData();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/trial-followup/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Lauf abgeschlossen: ${res.data?.sent || 0} versendet, ${res.data?.checked || 0} geprüft`);
      fetchData();
    } catch (e) {
      toast.error('Manueller Lauf fehlgeschlagen');
    } finally {
      setRunning(false);
    }
  };

  // Trainer-Anzahl pro Service als Warnung
  const trainerCountFor = (service) => (service?.trainer_ids?.length || 0);

  return (
    <Card data-testid="trial-followup-settings">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-600" />
              Probetraining Follow-Up
            </CardTitle>
            <CardDescription className="mt-1">
              Nach einem Probetraining ohne Folgetermin schicken wir dem Kunden automatisch einen
              Gratis-Voucher für ein zweites Probetraining bei einem anderen Trainer.
            </CardDescription>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(checked) => setSettings((p) => ({ ...p, enabled: checked }))}
            data-testid="trial-followup-enabled-switch"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Basis-Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="wait_days">Wartezeit nach Probetraining (Tage)</Label>
            <Input
              id="wait_days"
              type="number"
              min={1}
              max={90}
              value={settings.wait_days}
              onChange={(e) => setSettings((p) => ({ ...p, wait_days: parseInt(e.target.value || '14', 10) }))}
              data-testid="trial-followup-wait-days"
            />
            <p className="text-xs text-zinc-500 mt-1">Default 14 Tage — wann der Reminder ausgelöst wird.</p>
          </div>
          <div>
            <Label htmlFor="voucher_validity">Voucher gültig (Tage)</Label>
            <Input
              id="voucher_validity"
              type="number"
              min={1}
              max={365}
              value={settings.voucher_validity_days}
              onChange={(e) => setSettings((p) => ({ ...p, voucher_validity_days: parseInt(e.target.value || '30', 10) }))}
              data-testid="trial-followup-voucher-validity"
            />
          </div>
        </div>

        {/* Channels */}
        <div className="space-y-3">
          <Label>Kanäle</Label>
          <div className="flex items-center justify-between p-3 rounded-lg border bg-zinc-50">
            <div>
              <p className="text-sm font-medium">E-Mail senden</p>
              <p className="text-xs text-zinc-500">Template: <code className="text-xs">{settings.email_template_id}</code></p>
            </div>
            <Switch
              checked={!!settings.send_email}
              onCheckedChange={(checked) => setSettings((p) => ({ ...p, send_email: checked }))}
              data-testid="trial-followup-send-email"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border bg-zinc-50">
            <div>
              <p className="text-sm font-medium">WhatsApp senden</p>
              <p className="text-xs text-zinc-500">Template: <code className="text-xs">{settings.whatsapp_template_id}</code> (muss bei Meta approved sein)</p>
            </div>
            <Switch
              checked={!!settings.send_whatsapp}
              onCheckedChange={(checked) => setSettings((p) => ({ ...p, send_whatsapp: checked }))}
              data-testid="trial-followup-send-whatsapp"
            />
          </div>
        </div>

        {/* Eligible Services */}
        <div>
          <Label className="mb-2 block">Welche Services gelten als Probetraining?</Label>
          <p className="text-xs text-zinc-500 mb-3">
            Wähle die Services, deren Buchungen einen Follow-Up auslösen.
            <span className="text-amber-700 ml-1">⚠ Services mit nur 1 oder 0 Trainern senden KEINEN Reminder.</span>
          </p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto border rounded-lg p-2">
            {services.map((s) => {
              const checked = (settings.eligible_service_ids || []).includes(s.id);
              const tCount = trainerCountFor(s);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggleService(s.id)}
                  data-testid={`trial-followup-service-${s.id}`}
                  className={`w-full text-left flex items-center justify-between p-2 rounded-md transition ${
                    checked ? 'bg-emerald-50 border border-emerald-300' : 'bg-white border border-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                        checked ? 'border-emerald-600 bg-emerald-600' : 'border-zinc-400'
                      }`}
                    >
                      {checked && <span className="text-white text-[10px]">✓</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="text-[11px] text-zinc-500">{s.category}</p>
                    </div>
                  </div>
                  <Badge
                    className={
                      tCount > 1
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : tCount === 1
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-rose-100 text-rose-800 border-rose-300'
                    }
                  >
                    {tCount} {tCount === 1 ? 'Trainer' : 'Trainer'}
                  </Badge>
                </button>
              );
            })}
          </div>
          {(settings.eligible_service_ids || []).some((sid) => {
            const s = services.find((x) => x.id === sid);
            return s && (s.trainer_ids?.length || 0) < 2;
          }) && (
            <div className="mt-3 p-3 rounded-lg border border-amber-300 bg-amber-50 flex gap-2 items-start text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Mindestens ein gewählter Service hat weniger als 2 Trainer. Für diesen Service wird
                <strong> kein Reminder</strong> gesendet (per deinem Wunsch). Verlinke weitere Trainer in der Service-Verwaltung.
              </span>
            </div>
          )}
        </div>

        {/* Voucher-Audit */}
        {vouchers.length > 0 && (
          <div>
            <Label>Letzte Voucher ({vouchers.length})</Label>
            <div className="space-y-1 max-h-48 overflow-y-auto mt-2">
              {vouchers.slice(0, 8).map((v) => (
                <div
                  key={v.id}
                  className="flex justify-between items-center text-xs p-2 rounded border bg-zinc-50"
                  data-testid={`trial-followup-voucher-${v.id}`}
                >
                  <div>
                    <code className="font-mono text-[11px]">{v.code}</code>
                    <span className="ml-2 text-zinc-600">{v.customer_name || v.customer_email}</span>
                  </div>
                  <Badge variant={v.used ? 'default' : 'outline'} className={v.used ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'text-amber-700 border-amber-400'}>
                    {v.used ? 'eingelöst' : 'offen'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button onClick={handleSave} disabled={loading} data-testid="trial-followup-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Speichern…' : 'Speichern'}
          </Button>
          <Button
            variant="outline"
            onClick={handleRunNow}
            disabled={running || !settings.enabled}
            data-testid="trial-followup-run-now-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {running ? 'Läuft…' : 'Jetzt manuell laufen lassen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrialFollowupSettings;
